import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export default function MyLedger() {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    api.get('/ledger/my/')
      .then(res => setLedger(res.data))
      .catch(() => toast.error('Failed to load ledger.'))
      .finally(() => setLoading(false));
  }, []);

  // --- Filtering ---
  const filteredEntries = ledger ? ledger.entries.filter(e => {
    const q = search.toLowerCase();
    const matchesSearch = !q || e.transaction_number.toLowerCase().includes(q)
      || e.description.toLowerCase().includes(q)
      || e.entry_type_display.toLowerCase().includes(q);
    const matchesFrom = !dateFrom || e.date >= dateFrom;
    const matchesTo = !dateTo || e.date <= dateTo;
    return matchesSearch && matchesFrom && matchesTo;
  }) : [];

  const isFiltering = search || dateFrom || dateTo;
  const filteredDebit = filteredEntries.reduce((s, e) => s + Number(e.debit_amount), 0);
  const filteredCredit = filteredEntries.reduce((s, e) => s + Number(e.credit_amount), 0);
  const filteredBalance = filteredDebit - filteredCredit;

  const displayEntries = isFiltering ? filteredEntries : (ledger?.entries || []);
  const displayDebit = isFiltering ? filteredDebit : Number(ledger?.total_debit || 0);
  const displayCredit = isFiltering ? filteredCredit : Number(ledger?.total_credit || 0);
  const displayBalance = isFiltering ? filteredBalance : Number(ledger?.balance || 0);

  const downloadLedgerPDF = async () => {
    if (!ledger) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;
      const usableWidth = pageWidth - margin * 2;

      // Logo
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = '/NIS ABJ LOGO.jpg';
        });
        doc.addImage(logoImg, 'JPEG', margin, 8, 22, 22);
      } catch {}

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('NIGERIAN INSTITUTION OF SURVEYORS', 40, 15);
      doc.setFontSize(12);
      doc.text('FCT BRANCH — Member Ledger', 40, 22);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Customer ID: ${ledger.membership_number || 'N/A'}`, margin, 38);
      doc.text(`Customer Name: ${ledger.member_name}`, margin, 44);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, 50);
      const tableStartY = 56;

      // Column widths proportional to usable width
      const colDate = 28;
      const colTrans = 28;
      const colType = 20;
      const colDebit = 36;
      const colCredit = 36;
      const colBalance = 36;
      const colDesc = usableWidth - colDate - colTrans - colType - colDebit - colCredit - colBalance;

      const entries = displayEntries;
      const totalDebit = displayDebit;
      const totalCredit = displayCredit;
      const totalBalance = displayBalance;
      const totalBalanceStr = totalBalance > 0
        ? `-N${totalBalance.toLocaleString()}`
        : `N${Math.abs(totalBalance).toLocaleString()}`;

      const bodyRows = entries.map(e => {
        const bal = Number(e.balance);
        return [
          e.date,
          e.transaction_number,
          e.entry_type,
          e.description,
          e.debit_amount !== '0.00' ? `N${Number(e.debit_amount).toLocaleString()}` : '',
          e.credit_amount !== '0.00' ? `N${Number(e.credit_amount).toLocaleString()}` : '',
          bal > 0 ? `-N${bal.toLocaleString()}` : `N${Math.abs(bal).toLocaleString()}`,
        ];
      });

      bodyRows.push([
        { content: '', styles: {} },
        { content: '', styles: {} },
        { content: '', styles: {} },
        { content: 'Report Total', styles: { fontStyle: 'bold', halign: 'right' } },
        { content: `N${totalDebit.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: `N${totalCredit.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: totalBalanceStr, styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      autoTable(doc, {
        startY: tableStartY,
        margin: { left: margin, right: margin },
        tableWidth: usableWidth,
        head: [['Date', 'Trans No', 'Type', 'Description', 'Debit Amt', 'Credit Amt', 'Balance']],
        body: bodyRows,
        styles: { fontSize: 10, cellPadding: 3, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.2 },
        headStyles: { fillColor: [11, 93, 30], textColor: 255, fontStyle: 'bold', halign: 'center', cellPadding: 4 },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: {
          0: { cellWidth: colDate, halign: 'center' },
          1: { cellWidth: colTrans, halign: 'center' },
          2: { cellWidth: colType, halign: 'center' },
          3: { cellWidth: colDesc, halign: 'left' },
          4: { cellWidth: colDebit, halign: 'right' },
          5: { cellWidth: colCredit, halign: 'right' },
          6: { cellWidth: colBalance, halign: 'right' },
        },
        didParseCell: (data) => {
          const isLastRow = data.row.index === bodyRows.length - 1;
          // Debit column — red
          if (data.column.index === 4 && data.cell.raw !== '') {
            data.cell.styles.textColor = [220, 38, 38];
          }
          // Credit column — green
          if (data.column.index === 5 && data.cell.raw !== '') {
            data.cell.styles.textColor = [22, 163, 74];
          }
          // Balance column — red if debit balance, green if credit
          if (data.column.index === 6 && data.section === 'body') {
            if (isLastRow) {
              data.cell.styles.textColor = totalBalance > 0 ? [220, 38, 38] : [22, 163, 74];
            } else {
              const entryBalance = Number(entries[data.row.index]?.balance || 0);
              data.cell.styles.textColor = entryBalance > 0 ? [220, 38, 38] : [22, 163, 74];
            }
          }
          // Total row — grey background
          if (isLastRow) {
            data.cell.styles.fillColor = [235, 235, 235];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      doc.save(`My_Ledger_${ledger.membership_number || 'statement'}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF.');
    }
  };

  // === Export to Excel ===
  const exportExcel = () => {
    if (!ledger) return;
    try {
      const totalBalance = displayBalance;
      const headerRows = [
        { 'Date': 'NIGERIAN INSTITUTION OF SURVEYORS — FCT BRANCH', 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' },
        { 'Date': `Customer ID: ${ledger.membership_number || 'N/A'}`, 'Trans No': '', 'Type': '', 'Description': `Customer Name: ${ledger.member_name}`, 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' },
        { 'Date': `Generated: ${new Date().toLocaleDateString()}`, 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' },
        { 'Date': '', 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' },
      ];
      const rows = displayEntries.map(e => {
        const bal = Number(e.balance);
        return {
          'Date': e.date,
          'Trans No': e.transaction_number,
          'Type': e.entry_type,
          'Description': e.description,
          'Debit Amt': Number(e.debit_amount) || 0,
          'Credit Amt': Number(e.credit_amount) || 0,
          'Balance': bal > 0 ? `-₦${bal.toLocaleString()}` : `₦${Math.abs(bal).toLocaleString()}`,
        };
      });
      rows.push({
        'Date': '', 'Trans No': '', 'Type': '', 'Description': 'REPORT TOTAL',
        'Debit Amt': displayDebit,
        'Credit Amt': displayCredit,
        'Balance': totalBalance > 0 ? `-₦${totalBalance.toLocaleString()}` : `₦${Math.abs(totalBalance).toLocaleString()}`,
      });
      const ws = XLSX.utils.json_to_sheet([...headerRows, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'My Ledger');
      XLSX.writeFile(wb, `My_Ledger_${ledger.membership_number || 'statement'}.xlsx`);
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Failed to export Excel.');
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading ledger...</div>;

  if (!ledger) return <div className="text-center py-12 text-gray-400">Unable to load your ledger.</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Financial Ledger</h1>
          <p className="text-sm text-gray-500">Customer ID: <span className="font-mono font-semibold">{ledger.membership_number || 'N/A'}</span></p>
        </div>
        <div className="flex gap-2 flex-wrap self-start">
          <button onClick={downloadLedgerPDF}
            className="bg-purple-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
            Download PDF
          </button>
          <button onClick={exportExcel}
            className="bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-green-800 transition flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Export Excel
          </button>
        </div>
      </div>

      {/* Search & Date Range */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Search</label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by trans no, description, type..."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" />
          </div>
          {isFiltering && (
            <div className="flex items-end">
              <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}
                className="text-sm text-red-600 hover:text-red-800 font-semibold px-3 py-2 border border-red-200 rounded-lg hover:bg-red-50 transition">
                Clear Filters
              </button>
            </div>
          )}
        </div>
        {isFiltering && (
          <p className="text-xs text-gray-400 mt-2">Showing {displayEntries.length} of {ledger.entries.length} transactions</p>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 font-semibold uppercase">Total Charges</p>
          <p className="text-xl font-bold text-red-600">₦{displayDebit.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 font-semibold uppercase">Total Payments</p>
          <p className="text-xl font-bold text-green-600">₦{displayCredit.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 font-semibold uppercase">Outstanding Balance</p>
          <p className={`text-xl font-bold ${displayBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {displayBalance > 0 ? '-' : ''}₦{Math.abs(displayBalance).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Trans No</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Debit Amt</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Credit Amt</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {displayEntries.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-gray-400 text-sm">{isFiltering ? 'No matching transactions.' : 'No transactions yet.'}</td></tr>
            ) : (
              <>
                {displayEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{entry.date}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{entry.transaction_number}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                        entry.entry_type === 'SJ' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>{entry.entry_type_display}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.description}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">
                      {entry.debit_amount !== '0.00' ? `₦${Number(entry.debit_amount).toLocaleString()}` : ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                      {entry.credit_amount !== '0.00' ? `₦${Number(entry.credit_amount).toLocaleString()}` : ''}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-bold ${Number(entry.balance) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {Number(entry.balance) > 0 ? '-' : ''}₦{Math.abs(Number(entry.balance)).toLocaleString()}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td colSpan="4" className="px-4 py-3 text-sm text-right text-gray-700">{isFiltering ? 'Filtered Total' : 'Report Total'}</td>
                  <td className="px-4 py-3 text-sm text-right text-red-700">₦{displayDebit.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-700">₦{displayCredit.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-sm text-right font-bold ${displayBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>{displayBalance > 0 ? '-' : ''}₦{Math.abs(displayBalance).toLocaleString()}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
